import { cn } from '@clarix/ui';
import { TIME_BAND_ICON, TIME_BAND_ICON_CLASS, useTimeOfDay } from './time-of-day';
import type { HomeStatus, HomeStatusTone } from './types';

export interface HomeGreetingProps {
  /** Full name; only the first word is used. */
  userName?: string;
  /** Overrides the clock-derived greeting entirely (name still appended). */
  greeting?: string;
  /**
   * Density.
   *
   * `hero` is the home's own title: the greeting alone, set to read with the
   * assistant wordmark below it — no glyph, no date, no status line.
   * `bar` is the compact two-line block for a header strip, where the date
   * and the status line earn their space.
   */
  size?: 'hero' | 'bar';
  /** `bar` only — secondary label beside the greeting, e.g. "Thursday 23 July". */
  dateLabel?: string;
  /** `bar` only — the single status line. */
  status?: HomeStatus | null;
  /** `center` stacks it centred rather than flush left. */
  align?: 'start' | 'center';
  className?: string;
}

const STATUS_TONE_CLASS: Record<HomeStatusTone, string> = {
  neutral: 'text-muted-foreground',
  warn: 'text-warning-fg',
  good: 'text-success-fg',
};

/**
 * The greeting, following the clock and nothing else.
 *
 * See `size` for the two densities — the home uses `hero`, a header strip
 * uses `bar`.
 */
export function HomeGreeting({
  userName,
  greeting,
  size = 'bar',
  dateLabel,
  status,
  align = 'start',
  className,
}: HomeGreetingProps) {
  const { band, greeting: clockGreeting } = useTimeOfDay();
  const Glyph = TIME_BAND_ICON[band];

  const firstName = userName?.trim().split(/\s+/)[0];
  const base = greeting ?? clockGreeting;
  const line = firstName ? `${base}, ${firstName}` : base;

  // The home's title. Same type treatment AND the same accent as the
  // wordmark it sits above — held one step back in size only, so the two
  // read as one lockup.
  if (size === 'hero') {
    return (
      <p
        className={cn(
          'text-primary text-2xl font-bold tracking-tight sm:text-[1.75rem]',
          align === 'center' && 'text-center',
          className,
        )}
      >
        {line}
      </p>
    );
  }

  const toneClass = STATUS_TONE_CLASS[status?.tone ?? 'neutral'];

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2.5',
        align === 'center' && 'justify-center',
        className,
      )}
    >
      <Glyph
        className={cn('h-[22px] w-[22px] shrink-0', TIME_BAND_ICON_CLASS[band])}
        strokeWidth={1.6}
        aria-hidden
      />
      <div
        className={cn(
          'flex min-w-0 flex-col leading-tight',
          align === 'center' && 'items-center text-center',
        )}
      >
        <span className="text-foreground truncate text-sm font-semibold tracking-tight">
          {line}
          {dateLabel ? (
            <span className="text-muted-foreground ml-1.5 font-medium">{`· ${dateLabel}`}</span>
          ) : null}
        </span>

        {status ? (
          status.onAction ? (
            <button
              type="button"
              onClick={status.onAction}
              title={status.actionLabel ?? 'Act on this'}
              className={cn(
                'truncate text-left text-[11.5px] hover:underline focus-visible:underline focus-visible:outline-none',
                toneClass,
              )}
            >
              {status.text}
            </button>
          ) : (
            <span className={cn('truncate text-[11.5px]', toneClass)}>{status.text}</span>
          )
        ) : null}
      </div>
    </div>
  );
}
